import { useState } from 'react';
import RedirectRegistryTable from './RedirectRegistryTable';
import ResourceMeters from './ResourceMeters';
import type { RedirectEntry, ServerResources } from '@frontend/types/adminhub';

interface SystemPanelProps {
  expanded: boolean;
  status: 'up' | 'down' | 'unknown';
  redirectEntries: RedirectEntry[];
  serverResources: ServerResources;
  loadingResources: boolean;
  resourcesError: string;
  onToggle: () => void;
  fmtBytes: (bytes: number) => string;
  meterLevel: (pct: number) => string;
}

export default function SystemPanel({
  expanded,
  status,
  redirectEntries,
  serverResources,
  loadingResources,
  resourcesError,
  onToggle,
  fmtBytes,
  meterLevel
}: SystemPanelProps) {
  const [activeTab, setActiveTab] = useState<'redirect-registry' | 'server-resources'>('server-resources');

  return (
    <div className="ecosystem-card admin-widget-card">
      <button type="button" className="admin-widget-header" onClick={onToggle}>
        <div className="admin-widget-header-main">
          <h3 className="ecosystem-title">System</h3>
          <span className={`admin-status-badge status-${status}`}>{status}</span>
        </div>
        <span className="admin-widget-toggle">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded ? (
        <div className="admin-widget-body">
          <p className="ecosystem-description">Runtime checks and single sign-on redirect contracts for core services.</p>
          <div className="admin-tab-bar">
            <button
              type="button"
              className={`admin-tab-btn ${activeTab === 'redirect-registry' ? 'active' : ''}`}
              onClick={() => setActiveTab('redirect-registry')}
              aria-selected={activeTab === 'redirect-registry'}
            >
              Redirect Registry
            </button>
            <button
              type="button"
              className={`admin-tab-btn ${activeTab === 'server-resources' ? 'active' : ''}`}
              onClick={() => setActiveTab('server-resources')}
              aria-selected={activeTab === 'server-resources'}
            >
              Server Resources
            </button>
          </div>

          {activeTab === 'redirect-registry' ? (
            <div id="system-redirect-registry" className="admin-subsection">
              <h4 className="project-visibility-name">Redirect Registry</h4>
              <p className="project-visibility-meta">Generated from current portal service assumptions. Infra source of truth remains registry.yaml.</p>
              <RedirectRegistryTable entries={redirectEntries} />
            </div>
          ) : null}

          {activeTab === 'server-resources' ? (
            <div id="system-resources" className="admin-subsection">
              <h4 className="project-visibility-name">Server Resources</h4>
              <p className="project-visibility-meta">RAM, disk and container utilization from the portal host.</p>
              {loadingResources ? <p className="ecosystem-description">Loading server resources…</p> : null}
              {resourcesError ? <div className="settings-message settings-error">{resourcesError}</div> : null}
              {!loadingResources && !resourcesError ? (
                <ResourceMeters
                  serverResources={serverResources}
                  fmtBytes={fmtBytes}
                  meterLevel={meterLevel}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
